import React, { Fragment, useState, useEffect } from 'react';
import styles from './styles.scss';
import classnames from 'classnames';
import { ResourceStatus } from '../../types/resource';
import ErrorState from '../ErrorState';

import landing from '../../assets/images/landing.svg';

import SearchableSelectBox, {
  AsyncSearchableSelectBox,
} from '../SearchableSelectBox';
import { ALL_ORGANIZATIONS } from '../../types/all-organizations';
import { AppBar, Icons, Skeleton } from '@densityco/ui';
import colorVariables from '@densityco/ui/variables/colors.json';
import HomeStore from '../../rx-stores/home';
import { HomeTabs, HomeActionTypes } from '../../types/home';
import useRxStore from '../../utils/use-rx-store';
import useRxDispatch from '../../utils/use-rx-dispatch';

import { NAVBAR_SIDEBAR_ITEMS } from '../Navbar/Sidebar';

export function Home() {
  const homeState = useRxStore(HomeStore);
  const dispatch = useRxDispatch();

  const [open, setOpen] = useState(false);

  useEffect(() => {
    dispatch({ type: HomeActionTypes.HOME_CHANGE_PAGE });
    return () => {
      dispatch({ type: HomeActionTypes.HOME_CHANGE_SEARCH, search: '' });
    };
  }, []);

  const singleOrganization =
    homeState.organizationResource.status === ResourceStatus.COMPLETE &&
    homeState.organizationResource.data.organizations.length === 1;

  const dpuSearchBox =
    homeState.dpuResource.status === ResourceStatus.COMPLETE ? (
      <AsyncSearchableSelectBox
        placeholder="Search for a Sensor"
        choices={homeState.dpuResource.data.units.map(i => ({
          id: i.id,
          label: i.name,
          metric: i.organizationName,
          href: `#/${i.organizationId}/sensors/${i.hrefId}`,
        }))}
        popupWidth={476}
        selectedId={null}
        searchValue={homeState.search}
        onSearchChange={e => {
          dispatch({
            type: HomeActionTypes.HOME_CHANGE_SEARCH,
            search: e.target.value,
          });
        }}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
      />
    ) : null;

  return (
    <div className={styles.wrapper}>
      <div className={styles.options}>
        {homeState.organizationResource.status === ResourceStatus.COMPLETE &&
        homeState.dpuResource.status === ResourceStatus.COMPLETE ? (
          <Fragment>
            <h1>Welcome to Sensei</h1>
            {singleOrganization ? (
              <p>
                Configure units, evaluate spaces, or manage Multi Point
                Installations at{' '}
                <strong>
                  {homeState.organizationResource.data.organizations[0].name}
                </strong>
                .
              </p>
            ) : (
              <p>
                Choose an organization or search for specific DPUs you want to
                manage.
              </p>
            )}

            {!singleOrganization ? (
              // In regular mode, show tabs for dpu search and organizations.
              <Fragment>
                <div className={styles.tabBar}>
                  <div
                    className={classnames(styles.tab, {
                      [styles.active]:
                        homeState.activeTab === HomeTabs.DPU_SEARCH,
                    })}
                    onClick={() =>
                      dispatch({
                        type: HomeActionTypes.HOME_CHANGE_TAB,
                        tab: HomeTabs.DPU_SEARCH,
                      })
                    }
                    data-test-id="home-tab-dpu-search"
                  >
                    Sensor Search
                  </div>
                  <div
                    className={classnames(styles.tab, {
                      [styles.active]:
                        homeState.activeTab === HomeTabs.ORGANIZATIONS,
                    })}
                    onClick={() =>
                      dispatch({
                        type: HomeActionTypes.HOME_CHANGE_TAB,
                        tab: HomeTabs.ORGANIZATIONS,
                      })
                    }
                    data-test-id="home-tab-organizations"
                  >
                    Organizations
                  </div>
                </div>

                {homeState.activeTab === HomeTabs.DPU_SEARCH ? (
                  <div data-test-id="home-body-dpu-search">{dpuSearchBox}</div>
                ) : null}

                {homeState.activeTab === HomeTabs.ORGANIZATIONS ? (
                  <div data-test-id="home-body-organizations">
                    {homeState.organizationResource.status ===
                    ResourceStatus.COMPLETE ? (
                      <SearchableSelectBox
                        placeholder="Choose Organization"
                        choices={homeState.organizationResource.data.organizations.map(
                          organization => ({
                            id: organization.id,
                            label: organization.name,
                            metric: organization.number_of_units,
                            href: `#/${organization.id}/in-configuration`,
                          }),
                        )}
                        popupWidth={476}
                        selectedId={null}
                        targetIcon={Icons.Building}
                      />
                    ) : null}
                  </div>
                ) : null}
              </Fragment>
            ) : (
              // In single-organization-mode, show search box and a link to all pages for the organization.
              <Fragment>
                {dpuSearchBox}

                <div className={styles.homeOrSplitter}>OR</div>
                <nav className={styles.homeItemWrapper} data-test-id="home-single-organization-view">
                  {NAVBAR_SIDEBAR_ITEMS.map(item => {
                    if (
                      homeState.organizationResource.status !==
                      ResourceStatus.COMPLETE
                    ) {
                      return null;
                    }
                    return (
                      <a
                        key={item.name}
                        href={item.href(
                          homeState.organizationResource.data.organizations[0]
                            .id,
                        )}
                        className={styles.homeItem}
                        data-test-id="home-single-organization-item"
                      >
                        <item.icon color="currentColor" />
                        <span className={styles.homeItemChild}>
                          <span className={styles.homeItemName}>
                            {item.name}
                          </span>
                          <span className={styles.homeItemDescription}>
                            {item.description}
                          </span>
                        </span>
                        <Icons.ChevronRight color="currentColor" />
                      </a>
                    );
                  })}
                </nav>
              </Fragment>
            )}
          </Fragment>
        ) : null}

        {/* Loading state */}
        {homeState.dpuResource.status === ResourceStatus.LOADING ||
        homeState.organizationResource.status === ResourceStatus.LOADING ? (
          <Fragment>
            <h1>
              <Skeleton
                width={200}
                height={24}
                color={colorVariables.gray700}
              />
            </h1>
            <p>
              <Skeleton
                width={476}
                height={16}
                color={colorVariables.gray400}
              />
              <Skeleton
                width={150}
                height={16}
                color={colorVariables.gray400}
              />
            </p>
            <Skeleton width={476} height={40} color={colorVariables.gray200} />
          </Fragment>
        ) : null}

        {/* Error state */}
        {homeState.dpuResource.status === ResourceStatus.ERROR ? (
          <ErrorState error={homeState.dpuResource.error} />
        ) : homeState.organizationResource.status === ResourceStatus.ERROR ? (
          <ErrorState error={homeState.organizationResource.error} />
        ) : null}
      </div>
      <div className={styles.image}>
        <img src={landing} />
      </div>
    </div>
  );
}
